import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api, type Brand, type UploadInfo } from "../../api";
import {
  DRAG_MIME,
  ELEMENT_CATEGORIES,
  IMAGE_DRAG_PREFIX,
  imageOverlayTemplate,
  type DragPayload,
  type ElementCategoryId,
  type ElementEntry,
  type ElementTemplate,
} from "./elementCatalog";
import { Tooltip } from "./Tooltip";

type Props = {
  /**
   * Click handler — adds the element at the playhead (default position).
   * Use this for users who don't want to drag.
   */
  onAdd: (template: ElementTemplate) => void;
};

/**
 * Sidebar panel that lists every reusable overlay template (text presets,
 * shapes, icons, badges) and lets the user either click to insert at the
 * playhead OR drag straight onto the preview / timeline.
 *
 * The drag payload is a serialized {@link DragPayload} — the receiving drop
 * handler in `PlayerEditOverlay` looks the entry up via `findElementById`.
 */
export const ElementLibrary: React.FC<Props> = ({ onAdd }) => {
  const [tab, setTab] = useState<ElementCategoryId>("text");
  const [query, setQuery] = useState("");

  const activeCat = ELEMENT_CATEGORIES.find((c) => c.id === tab) ?? ELEMENT_CATEGORIES[0];
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return activeCat.entries;
    return activeCat.entries.filter(
      (e) =>
        e.label.toLowerCase().includes(q) ||
        e.preview.toLowerCase().includes(q) ||
        (e.hint?.toLowerCase().includes(q) ?? false),
    );
  }, [activeCat, query]);

  const onDragStart = (e: React.DragEvent, entry: ElementEntry): void => {
    const payload: DragPayload = { elementId: entry.id, category: entry.category };
    // Use the custom MIME so we can distinguish from arbitrary text drags
    // anywhere in the app. text/plain is also set as a fallback for browsers
    // that strip unknown MIMEs.
    e.dataTransfer.setData(DRAG_MIME, JSON.stringify(payload));
    e.dataTransfer.setData("text/plain", entry.label);
    e.dataTransfer.effectAllowed = "copy";
  };

  return (
    <div className="element-library">
      <div className="element-library-tabs">
        {ELEMENT_CATEGORIES.map((c) => (
          <button
            key={c.id}
            type="button"
            className={`element-library-tab ${tab === c.id ? "active" : ""}`}
            onClick={() => setTab(c.id)}
            title={c.label}
          >
            <span className="element-library-tab-glyph">{c.glyph}</span>
            <span>{c.label}</span>
          </button>
        ))}
      </div>

      <input
        className="element-library-search"
        type="search"
        placeholder={`${activeCat.label} suchen…`}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
      />

      {tab === "image" ? (
        <ImageLibraryPanel onAdd={onAdd} query={query} />
      ) : (
        <div className={`element-library-grid kind-${activeCat.id}`}>
          {filtered.map((entry) => (
            <Tooltip key={entry.id} title={entry.label} description={entry.hint ?? "Ziehen oder klicken zum Einfügen"}>
              <button
                type="button"
                className={`element-card kind-${entry.category}`}
                draggable
                onDragStart={(e) => onDragStart(e, entry)}
                onClick={() => onAdd(entry.build())}
                data-tip-host="true"
              >
                <span className="element-card-preview">{entry.preview}</span>
                <span className="element-card-label">{entry.label}</span>
              </button>
            </Tooltip>
          ))}
          {filtered.length === 0 ? (
            <div className="element-library-empty">Keine Treffer für „{query}"</div>
          ) : null}
        </div>
      )}
    </div>
  );
};

/* ============================================================ *
 *  IMAGE PANEL — Brand-Logo + User-Uploads                       *
 *  ------------------------------------------------------------ *
 *  - Upload-Tile (multi-file) lädt sofort hoch & fügt das erste  *
 *    Bild als Layer ein.                                         *
 *  - Brand-Logo erscheint als erste Karte, sofern verfügbar.     *
 *  - Alle vorherigen Uploads sind ziehbar/klickbar.              *
 * ============================================================ */

const ImageLibraryPanel: React.FC<{
  onAdd: (template: ElementTemplate) => void;
  query: string;
}> = ({ onAdd, query }) => {
  const [uploads, setUploads] = useState<UploadInfo[] | null>(null);
  const [brand, setBrand] = useState<Brand | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement | null>(null);

  const refresh = useCallback(async () => {
    try {
      const r = await api.uploadsList();
      setUploads(r.items.filter((it) => it.kind === "image"));
    } catch (e) {
      setErr((e as Error).message);
    }
  }, []);

  useEffect(() => {
    void refresh();
    void api.brand().then(setBrand).catch(() => {});
  }, [refresh]);

  const upload = useCallback(
    async (files: FileList | null) => {
      if (!files || files.length === 0) return;
      setBusy(true);
      setErr(null);
      try {
        const r = await api.uploadFiles(files);
        const firstImage = r.uploaded.find((u) => u.kind === "image");
        if (firstImage) onAdd(imageOverlayTemplate(firstImage.url));
        await refresh();
      } catch (e) {
        setErr((e as Error).message);
      } finally {
        setBusy(false);
      }
    },
    [onAdd, refresh],
  );

  const onDelete = useCallback(
    async (filename: string) => {
      if (!confirm(`„${filename}" wirklich aus den Uploads löschen?`)) return;
      try {
        await api.uploadsDelete(filename);
        await refresh();
      } catch (e) {
        setErr((e as Error).message);
      }
    },
    [refresh],
  );

  const onImageDragStart = (e: React.DragEvent, url: string, label: string): void => {
    const payload: DragPayload = { elementId: `${IMAGE_DRAG_PREFIX}${url}`, category: "image" };
    e.dataTransfer.setData(DRAG_MIME, JSON.stringify(payload));
    e.dataTransfer.setData("text/plain", label);
    e.dataTransfer.effectAllowed = "copy";
  };

  const brandLogoUrl: string | null = brand?.logo
    ? `/brand/${brand.logo.split("/").pop()}`
    : null;

  const q = query.trim().toLowerCase();
  const matchesQuery = (s: string): boolean => !q || s.toLowerCase().includes(q);

  const showBrandLogo = brandLogoUrl && (matchesQuery("brand") || matchesQuery("logo"));
  const visibleUploads = (uploads ?? []).filter((u) => matchesQuery(u.filename));
  const hasAny = (uploads !== null && uploads.length > 0) || !!brandLogoUrl;

  return (
    <div className="element-library-grid kind-image">
      <Tooltip
        title="Bild / Logo hochladen"
        description="PNG, JPG, SVG oder WebP. Mehrere Dateien gleichzeitig möglich. Das erste landet sofort im Preview."
      >
        <button
          type="button"
          className="element-card kind-image upload-card"
          onClick={() => fileRef.current?.click()}
          disabled={busy}
          data-tip-host="true"
        >
          <span className="element-card-preview">{busy ? "⏳" : "⬆"}</span>
          <span className="element-card-label">{busy ? "Lade…" : "Hochladen"}</span>
        </button>
      </Tooltip>
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        multiple
        style={{ display: "none" }}
        onChange={(e) => {
          void upload(e.target.files);
          e.target.value = "";
        }}
      />

      {showBrandLogo && brandLogoUrl ? (
        <Tooltip
          title="Brand-Logo"
          description="Aktuelles Logo aus deiner CI. Ziehen oder klicken zum Einfügen."
        >
          <button
            type="button"
            className="element-card kind-image"
            draggable
            onDragStart={(e) => onImageDragStart(e, brandLogoUrl, "Brand-Logo")}
            onClick={() => onAdd(imageOverlayTemplate(brandLogoUrl, { widthPct: 22, heightPct: 22 }))}
            data-tip-host="true"
          >
            <span className="element-card-preview image">
              <img src={brandLogoUrl} alt="" />
            </span>
            <span className="element-card-label">Brand-Logo</span>
          </button>
        </Tooltip>
      ) : null}

      {uploads === null ? (
        <div className="element-library-empty">Lade Uploads…</div>
      ) : null}

      {visibleUploads.map((u) => (
        <Tooltip key={u.filename} title={u.filename} description="Ziehen oder klicken zum Einfügen">
          <div className="element-card-wrap">
            <button
              type="button"
              className="element-card kind-image"
              draggable
              onDragStart={(e) => onImageDragStart(e, u.url, u.filename)}
              onClick={() => onAdd(imageOverlayTemplate(u.url))}
              data-tip-host="true"
            >
              <span className="element-card-preview image">
                <img src={u.url} alt="" />
              </span>
              <span className="element-card-label">
                {u.filename.length > 18 ? `${u.filename.slice(0, 16)}…` : u.filename}
              </span>
            </button>
            <button
              type="button"
              className="element-card-del"
              onClick={(e) => {
                e.stopPropagation();
                void onDelete(u.filename);
              }}
              title="Aus Uploads löschen"
              aria-label="Löschen"
            >
              🗑
            </button>
          </div>
        </Tooltip>
      ))}

      {uploads !== null && visibleUploads.length === 0 && !showBrandLogo ? (
        <div className="element-library-empty">
          {q ? `Keine Treffer für „${query}"` : hasAny
            ? "Nichts zu zeigen."
            : "Lade dein erstes Bild oder Logo über „⬆ Hochladen“ hoch."}
        </div>
      ) : null}

      {err ? <div className="element-library-empty">⚠ {err}</div> : null}
    </div>
  );
};

/** Look up an element by id — used by the drop handler to materialize a
 *  fresh template after the drag finished.
 *
 *  Supports a synthetic `image:<url>` id-form for ad-hoc image overlays
 *  (brand logo / user uploads) which aren't enumerated in the static
 *  catalog. The url is decoded back into a fresh image template per drop.
 */
export function findElementById(id: string): ElementEntry | null {
  if (id.startsWith(IMAGE_DRAG_PREFIX)) {
    const url = id.slice(IMAGE_DRAG_PREFIX.length);
    if (!url) return null;
    return {
      id,
      label: "Bild",
      preview: "🖼",
      category: "image",
      build: () => imageOverlayTemplate(url),
    };
  }
  for (const cat of ELEMENT_CATEGORIES) {
    const hit = cat.entries.find((e) => e.id === id);
    if (hit) return hit;
  }
  return null;
}
